import type { QueryClient } from '@tanstack/react-query';
import {
    CAROUSEL_DATA_BASE_CACHE_KEY,
    isUserSpecificCarousel,
} from '@tn/foundation/entities/hubs/utils/constants/carousels';
import { appLogger } from '@tn/foundation/utils/app-logger/appLogger';

const CAROUSEL_DATA_KEY_PREFIX = `${CAROUSEL_DATA_BASE_CACHE_KEY}-`;

type CarouselQueryKeyParams = {
    carouselParams?: {
        isMyList?: boolean | null;
        model?: string | null;
    };
};

type CarouselQueryData = {
    carouselData?: {
        result?: {
            data?: unknown[] | null;
            hasMore?: boolean | null;
            total?: number | null;
        } | null;
    } | null;
};

/** Reads carouselParams from a carousel-data query key ([cacheKey, dataParams]). */
function getCarouselParamsFromQueryKey(
    queryKey: unknown,
): CarouselQueryKeyParams['carouselParams'] {
    if (!Array.isArray(queryKey)) {
        return undefined;
    }
    const dataParams = queryKey[1] as CarouselQueryKeyParams | undefined;
    return dataParams?.carouselParams;
}

/**
 * User-specific rails (recent channels, keep watching, watch again). Scoped to `carousel-data-*`
 * caches: poster grids share the `[cacheKey, { carouselParams }]` key shape but store RequestPage
 * data the rail-shaped clear below would corrupt. My-list rails are excluded too:
 * `invalidateMyListAfterAuthChange` owns their auth-change refresh.
 */
export function isUserSpecificCarouselQuery(query: { queryKey: unknown }): boolean {
    if (!Array.isArray(query.queryKey)) {
        return false;
    }
    const [cacheKey] = query.queryKey;
    if (typeof cacheKey !== 'string' || !cacheKey.startsWith(CAROUSEL_DATA_KEY_PREFIX)) {
        return false;
    }
    const carouselParams = getCarouselParamsFromQueryKey(query.queryKey);
    if (!carouselParams || carouselParams.isMyList === true) {
        return false;
    }
    return isUserSpecificCarousel(carouselParams.model);
}

/**
 * Writes an empty result into every matching entry — even empty or in-flight ones, whose write is
 * what resets tiles accumulated from earlier pages — so rows collapse even if the refetch fails.
 */
function clearUserSpecificCarouselCaches(queryClient: QueryClient): void {
    for (const [queryKey, data] of queryClient.getQueriesData<CarouselQueryData>({
        predicate: isUserSpecificCarouselQuery,
    })) {
        const result = data?.carouselData?.result;
        queryClient.setQueryData<CarouselQueryData>(queryKey, {
            ...data,
            carouselData: {
                ...data?.carouselData,
                result: { ...result, data: [], hasMore: false, total: 0 },
            },
        });
    }
}

/**
 * Clears then refetches user-specific rails (active and inactive) after sign-in/out so a page
 * reached without a reload drops the previous identity's tiles. Never rejects the auth change.
 */
export async function refreshUserSpecificCarouselsAfterAuthChange(
    queryClient: QueryClient,
): Promise<void> {
    try {
        // Cancel first so a fetch issued under the previous identity can't land after the clear.
        await queryClient.cancelQueries({ predicate: isUserSpecificCarouselQuery });
        clearUserSpecificCarouselCaches(queryClient);
        await queryClient.invalidateQueries({
            predicate: isUserSpecificCarouselQuery,
            refetchType: 'none',
        });
        await queryClient.refetchQueries({
            predicate: isUserSpecificCarouselQuery,
            type: 'all',
        });
    } catch (error) {
        appLogger.error(
            'User-specific carousel refresh failed after auth change',
            { component: 'refreshUserSpecificCarouselsAfterAuthChange' },
            error,
        );
    }
}
